import type { PlayableTrack } from '../../stores/player';
import type { Credit } from './CreditsEditor';

/**
 * A track as `GET /tracks/{id}` returns it.
 *
 * ## One shape, because there is one endpoint
 *
 * The page and the drawer ask for the same thing at the same URL and were each
 * describing the answer separately - the drawer's version listed eleven fields
 * and the page's twenty-two. Which read as a drawer that *could not* show
 * credits or tempo, when in fact it had been sent both and thrown them away.
 *
 * A type is not what makes a field appear on screen, but a short one is how a
 * component quietly stops knowing what it was given.
 */
export interface TrackDetail extends PlayableTrack {
  slug: string;
  /**
   * The words, rendered exactly as they were typed - see `Lyrics`.
   *
   * Null for an instrumental, which is most records, and absent from the
   * lighter shapes that never select the column.
   */
  lyrics?: string | null;
  /** A YouTube video for the track. Null when there is none; see `TrackVideo`. */
  video?: { provider: string; id: string; url: string } | null;
  description: string | null;
  status: string;
  failure_reason: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  explicit: boolean;
  /**
   * How the record was made, as the artist states it.
   *
   * Null means not stated, which every track uploaded before the question
   * existed will be. It is never a synonym for `performed` - see
   * `provenance.tsx`.
   */
  provenance: 'performed' | 'ai_assisted' | 'ai_generated' | null;
  bpm: number | null;
  key_signature: string | null;
  loudness_lufs: number | null;
  play_count: number;
  like_count: number;
  created_at: string;
  is_owner: boolean;
  favorited: boolean;
  reposted: boolean;
  repost_count: number;
  credits: Credit[];
  genre_id: number | null;
  artwork_small: string | null;
}

/**
 * The subset the player needs, carried across rather than fetched again.
 *
 * `favorited` and `like_count` come with it so the dock's heart is drawn
 * correctly the moment playback starts, instead of after a request of its own.
 */
export function toPlayable(track: TrackDetail): PlayableTrack {
  return {
    id: track.id,
    title: track.title,
    duration_ms: track.duration_ms,
    waveform_url: track.waveform_url,
    artwork: track.artwork,
    artwork_small: track.artwork_small,
    owner: track.owner,
    favorited: track.favorited,
    like_count: track.like_count,
  };
}
